import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Fab,
  Slide,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  CheckCircle as CompleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  LocalHospital as DiagnosisIcon,
  AccessTime as TimeIcon,
  StickyNote2 as NotesIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Description as DocIcon,
  InsertDriveFile as FileIcon,
  CloudDownload as DownloadIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { orderService } from "../../services/orderService";
import authService from "../../services/authService";

// Slide transition for mobile dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MedicalProfessionalOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [completeDialog, setCompleteDialog] = useState(false);
  const [completing, setCompleting] = useState(false);

  // File states
  const [orderFiles, setOrderFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  useEffect(() => {
    if (order) {
      fetchOrderFiles();
    }
  }, [order]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await orderService.getOrderById(orderId);
      setOrder(response);
    } catch (err) {
      console.error("Error fetching order details:", err);
      setError("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await fetch(`/api/v1/orders/${orderId}/files`, {
        headers: {
          Authorization: `Bearer ${authService.getToken()}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const files = await response.json();
        setOrderFiles(files);
      } else {
        console.error("Failed to fetch files");
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleCompleteClick = () => {
    setCompleteDialog(true);
  };

  const handleCompleteConfirm = async () => {
    setCompleting(true);
    try {
      await orderService.updateOrderStatus(orderId, {
        status: "completed",
        notes: "Order completed by medical professional",
      });

      setSuccess("Order completed successfully!");
      setCompleteDialog(false);

      setTimeout(() => {
        navigate("/dashboard");
      }, 2000);
    } catch (err) {
      console.error("Error completing order:", err);
      setError(err.message || "Failed to complete order");
      setCompleteDialog(false);
    } finally {
      setCompleting(false);
    }
  };

  const handleCompleteCancel = () => {
    setCompleteDialog(false);
  };

  const openGoogleMaps = (address) => {
    if (address) {
      const mapsUrl = `https://www.google.com/maps/dir/Current+Location/${encodeURIComponent(
        address
      )}`;
      window.open(mapsUrl, "_blank");
    }
  };

  const makePhoneCall = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  const getPatientAddress = (patient) => {
    if (!patient) return "Address not available";
    const parts = [
      patient.address,
      patient.city,
      patient.state,
      patient.zip_code,
    ].filter(Boolean);
    return parts.join(", ");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return "Unknown";
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  };

  const getWindowTime = () => {
    // Use the actual scheduled time fields
    if (order?.scheduled_time) {
      const startTime = order.scheduled_time;
      const endTime = order.scheduled_time_end;
      if (endTime) {
        return `${startTime} - ${endTime}`;
      }
      return startTime;
    }
    // Fallback: try to parse from special_notes
    if (order?.special_notes) {
      const timePattern = /(\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2})/i;
      const match = order.special_notes.match(timePattern);
      if (match) return match[1];
    }
    return "Not specified";
  };

  // FILE HANDLING FUNCTIONS

  const getFileIcon = (mimeType, size = "medium") => {
    const iconProps = {
      fontSize: size,
      sx: { fontSize: isMobile ? 32 : undefined },
    };

    if (!mimeType) return <FileIcon {...iconProps} />;

    if (mimeType.includes("pdf"))
      return <PdfIcon {...iconProps} color="error" />;
    if (mimeType.includes("image"))
      return <ImageIcon {...iconProps} color="primary" />;
    if (mimeType.includes("word") || mimeType.includes("document"))
      return <DocIcon {...iconProps} color="info" />;

    return <FileIcon {...iconProps} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const canPreviewFile = (mimeType) => {
    if (!mimeType) return false;
    return mimeType.includes("image") || mimeType.includes("pdf");
  };

  const handleViewFile = async (file) => {
    setCurrentFile(file);
    setPreviewError("");
    setZoomLevel(1);

    if (!canPreviewFile(file.mime_type)) {
      // If can't preview, just download
      handleDownloadFile(file);
      return;
    }

    setPreviewDialog(true);
    setLoadingPreview(true);

    try {
      const response = await fetch(
        `/api/v1/orders/${orderId}/files/${file.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${authService.getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load file");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setFileContent(url);
    } catch (error) {
      console.error("Error loading file preview:", error);
      setPreviewError("Failed to load file preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const response = await fetch(
        `/api/v1/orders/${orderId}/files/${file.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${authService.getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("File download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess("File downloaded successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error downloading file:", error);
      setError("File download failed");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handleClosePreview = () => {
    setPreviewDialog(false);
    setCurrentFile(null);
    if (fileContent) {
      URL.revokeObjectURL(fileContent);
      setFileContent(null);
    }
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (error && !order) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Order not found</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/dashboard")}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  const patient = order.patient;
  const patientName = patient
    ? `${patient.first_name} ${patient.last_name}`
    : "Unknown Patient";
  const patientAddress = getPatientAddress(patient);

  const InfoRow = ({ label, value, icon, onClick, clickable = false }) => (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        sx={{
          color: "#9e9e9e",
          fontWeight: 500,
          fontSize: isMobile ? "0.7rem" : "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 0.5,
          cursor: clickable ? "pointer" : "default",
          "&:hover": clickable ? { backgroundColor: "#f5f5f5" } : {},
          borderRadius: clickable ? 1 : 0,
          p: clickable ? 1 : 0,
          mx: clickable ? -1 : 0,
        }}
        onClick={onClick}
      >
        {icon && <Box sx={{ mr: 1, color: "#757575" }}>{icon}</Box>}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: "#212121",
            fontSize: isMobile ? "0.95rem" : "1rem",
            flex: 1,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: "#fafafa", minHeight: "100vh", pb: 10 }}>
      {/* Success and Error Messages */}
      {success && (
        <Alert severity="success" sx={{ m: 2, mb: 0 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ m: 2, mb: 0 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: isMobile ? 1.5 : 2,
          backgroundColor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={() => navigate("/dashboard")}
            sx={{ mr: 1, p: 1 }}
          >
            <BackIcon />
          </IconButton>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            sx={{ fontWeight: 600 }}
          >
            {order.order_number}
          </Typography>
        </Box>
        {order.status === "assigned" && (
          <Button
            variant="contained"
            color="success"
            size={isMobile ? "small" : "medium"}
            startIcon={<CompleteIcon />}
            onClick={handleCompleteClick}
            sx={{ borderRadius: 2 }}
          >
            {isMobile ? "Complete" : "Complete Order"}
          </Button>
        )}
      </Box>

      {/* Main Content */}
      <Box sx={{ p: isMobile ? 1.5 : 2 }}>
        <Paper
          elevation={1}
          sx={{
            p: isMobile ? 2 : 3,
            borderRadius: 2,
            backgroundColor: "#fff",
          }}
        >
          {/* Window Time */}
          <InfoRow
            label="Window Time"
            value={getWindowTime()}
            icon={<TimeIcon />}
          />

          <Divider sx={{ my: 2 }} />

          {/* Patient ID */}
          <InfoRow
            label="Patient ID"
            value={patient?.patient_id || patient?.encoded_id || "N/A"}
            icon={<PersonIcon />}
          />

          <Divider sx={{ my: 2 }} />

          {/* Full Name */}
          <InfoRow label="Patient Name" value={patientName} />

          <Divider sx={{ my: 2 }} />

          {/* Date of Birth */}
          <InfoRow
            label="Date of Birth"
            value={
              patient?.date_of_birth
                ? `${formatDate(patient.date_of_birth)} (Age: ${calculateAge(
                    patient.date_of_birth
                  )})`
                : "Not specified"
            }
          />

          <Divider sx={{ my: 2 }} />

          {/* Gender */}
          <InfoRow label="Gender" value={patient?.gender || "Not specified"} />

          <Divider sx={{ my: 2 }} />

          {/* Exam Type */}
          <InfoRow
            label="Exam Type"
            value={
              order.exams && order.exams.length > 0
                ? order.exams
                    .map((exam) => {
                      const name = exam.exam_type?.name || "Unknown Exam";
                      return exam.notes ? `${name} (${exam.notes})` : name;
                    })
                    .join(", ")
                : "No exams specified"
            }
            icon={<DiagnosisIcon />}
          />

          <Divider sx={{ my: 2 }} />

          {/* Diagnosis */}
          <InfoRow
            label="Diagnosis"
            value={
              order.diagnoses && order.diagnoses.length > 0
                ? order.diagnoses
                    .map((d) => {
                      const diag = d.diagnosis || d;
                      const code = diag.code || "";
                      const desc = diag.description || "";
                      const primary = d.primary ? " (Primary)" : "";
                      return code ? `${code} - ${desc}${primary}` : `${desc}${primary}`;
                    })
                    .join(", ")
                : "No diagnosis specified"
            }
          />

          <Divider sx={{ my: 2 }} />

          {/* Referring Physician */}
          <InfoRow
            label="Referring Physician"
            value={
              order.ordering_physician
                ? `Dr. ${order.ordering_physician.first_name} ${order.ordering_physician.last_name}`
                : "Not specified"
            }
          />

          <Divider sx={{ my: 2 }} />

          {/* Health Company + Phone */}
          <InfoRow
            label="Health Company"
            value={order.ordering_health_company || order.ordering_physician?.facility_name || "Not specified"}
          />

          {order.ordering_physician?.phone_number && (
            <InfoRow
              label="Company Phone"
              value={order.ordering_physician.phone_number}
              icon={<PhoneIcon />}
              onClick={() =>
                makePhoneCall(order.ordering_physician.phone_number)
              }
              clickable={true}
            />
          )}

          <Divider sx={{ my: 2 }} />

          {/* Patient Address */}
          <Box sx={{ mb: 2 }}>
            <InfoRow
              label="Patient Address"
              value={patientAddress}
              icon={<LocationIcon />}
              onClick={() => openGoogleMaps(patientAddress)}
              clickable={true}
            />
            {order.address_notes && (
              <Box
                sx={{
                  mt: 1,
                  ml: isMobile ? 0 : 4,
                  p: 1.5,
                  backgroundColor: "#fff3e0",
                  borderRadius: 1,
                  borderLeft: "4px solid #ff9800",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#9e9e9e",
                    fontWeight: 500,
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  📍 Address Notes
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#e65100",
                    fontSize: isMobile ? "0.9rem" : "0.95rem",
                  }}
                >
                  {order.address_notes}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Patient Phone */}
          {patient?.phone_number && (
            <>
              <InfoRow
                label="Patient Phone"
                value={patient.phone_number}
                icon={<PhoneIcon />}
                onClick={() => makePhoneCall(patient.phone_number)}
                clickable={true}
              />
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Notes */}
          {order.special_notes && (
            <>
              <InfoRow
                label="Notes"
                value={order.special_notes}
                icon={<NotesIcon />}
              />
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* ATTACHED DOCUMENTS SECTION */}
          <Box sx={{ mt: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <AttachFileIcon color="primary" />
              <Typography
                variant={isMobile ? "body1" : "h6"}
                color="primary"
                fontWeight={600}
              >
                Attached Documents
              </Typography>
              {orderFiles.length > 0 && (
                <Chip label={orderFiles.length} size="small" color="primary" />
              )}
            </Box>

            {loadingFiles ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={isMobile ? 30 : 40} />
              </Box>
            ) : orderFiles.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  No documents attached to this order.
                </Typography>
              </Alert>
            ) : (
              <Box>
                {orderFiles.map((file) => (
                  <Card
                    key={file.id}
                    variant="outlined"
                    sx={{
                      mb: isMobile ? 1.5 : 2,
                      borderRadius: 2,
                      "&:hover": {
                        boxShadow: 2,
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: isMobile ? 1.5 : 2,
                        "&:last-child": { pb: isMobile ? 1.5 : 2 },
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={isMobile ? 1.5 : 2}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: isMobile ? 48 : 56,
                            height: isMobile ? 48 : 56,
                            backgroundColor: "grey.100",
                            borderRadius: 1,
                          }}
                        >
                          {getFileIcon(
                            file.mime_type,
                            isMobile ? "medium" : "large"
                          )}
                        </Box>

                        <Box flex={1} minWidth={0}>
                          <Typography
                            variant={isMobile ? "body2" : "body1"}
                            fontWeight={600}
                            noWrap
                            sx={{ mb: 0.5 }}
                          >
                            {file.file_name}
                          </Typography>

                          <Box
                            display="flex"
                            gap={isMobile ? 1 : 2}
                            flexWrap="wrap"
                          >
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
                            >
                              {formatFileSize(file.file_size)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
                            >
                              {new Date(file.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          display="flex"
                          gap={0.5}
                          flexDirection={isMobile ? "column" : "row"}
                        >
                          {canPreviewFile(file.mime_type) && (
                            <IconButton
                              color="primary"
                              onClick={() => handleViewFile(file)}
                              size={isMobile ? "large" : "medium"}
                              sx={{
                                backgroundColor: "primary.light",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "primary.main",
                                },
                                width: isMobile ? 48 : 40,
                                height: isMobile ? 48 : 40,
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          )}

                          <IconButton
                            color="secondary"
                            onClick={() => handleDownloadFile(file)}
                            size={isMobile ? "large" : "medium"}
                            sx={{
                              backgroundColor: "secondary.light",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "secondary.main",
                              },
                              width: isMobile ? 48 : 40,
                              height: isMobile ? 48 : 40,
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              <Typography
                variant="caption"
                sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
              >
                <strong>Secure Access:</strong> All documents are encrypted and
                access is logged for HIPAA compliance.
              </Typography>
            </Alert>
          </Box>
        </Paper>
      </Box>

      {/* Complete Order Confirmation Dialog */}
      <Dialog
        open={completeDialog}
        onClose={handleCompleteCancel}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Transition : undefined}
      >
        <DialogTitle>Complete Order</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to mark this order as completed?
          </Typography>
          <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
            <Typography variant="body2">
              <strong>Order:</strong> {order.order_number}
            </Typography>
            <Typography variant="body2">
              <strong>Patient:</strong> {patientName}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. The order will be marked as completed
            and you will be redirected back to your dashboard.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCompleteCancel} disabled={completing}>
            Cancel
          </Button>
          <Button
            onClick={handleCompleteConfirm}
            variant="contained"
            color="success"
            disabled={completing}
          >
            {completing ? "Completing..." : "Yes, Complete Order"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FILE PREVIEW DIALOG - MOBILE OPTIMIZED */}
      <Dialog
        open={previewDialog}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Transition : undefined}
        PaperProps={{
          sx: {
            height: isMobile ? "100%" : "90vh",
            m: isMobile ? 0 : 2,
          },
        }}
      >
        {isMobile ? (
          // Mobile: Full screen with app bar
          <>
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={handleClosePreview}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
                <Typography
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                  component="div"
                >
                  {currentFile?.file_name}
                </Typography>
                <IconButton
                  color="inherit"
                  onClick={() => handleDownloadFile(currentFile)}
                >
                  <DownloadIcon />
                </IconButton>
              </Toolbar>
            </AppBar>

            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                backgroundColor: "#000",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {loadingPreview ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                >
                  <CircularProgress size={60} sx={{ color: "white" }} />
                  <Typography color="white">Loading preview...</Typography>
                </Box>
              ) : previewError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {previewError}
                </Alert>
              ) : fileContent && currentFile ? (
                <>
                  {currentFile.mime_type?.includes("image") ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "auto",
                        p: 2,
                      }}
                    >
                      <img
                        src={fileContent}
                        alt={currentFile.file_name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          transform: `scale(${zoomLevel})`,
                          transition: "transform 0.2s",
                        }}
                      />
                    </Box>
                  ) : currentFile.mime_type?.includes("pdf") ? (
                    <iframe
                      src={fileContent}
                      title={currentFile.file_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                    />
                  ) : null}

                  {/* Zoom Controls for Images */}
                  {currentFile.mime_type?.includes("image") && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        gap: 1,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        borderRadius: 3,
                        p: 1,
                      }}
                    >
                      <Fab
                        size="small"
                        onClick={handleZoomOut}
                        disabled={zoomLevel <= 0.5}
                        sx={{ backgroundColor: "white" }}
                      >
                        <ZoomOutIcon />
                      </Fab>
                      <Chip
                        label={`${Math.round(zoomLevel * 100)}%`}
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          minWidth: 70,
                        }}
                      />
                      <Fab
                        size="small"
                        onClick={handleZoomIn}
                        disabled={zoomLevel >= 3}
                        sx={{ backgroundColor: "white" }}
                      >
                        <ZoomInIcon />
                      </Fab>
                    </Box>
                  )}
                </>
              ) : null}
            </Box>
          </>
        ) : (
          // Desktop: Regular dialog
          <>
            <DialogTitle>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" gap={1}>
                  {currentFile && getFileIcon(currentFile.mime_type)}
                  <Typography variant="h6" noWrap sx={{ maxWidth: 400 }}>
                    {currentFile?.file_name}
                  </Typography>
                </Box>
                <Box display="flex" gap={1}>
                  <IconButton
                    onClick={() => handleDownloadFile(currentFile)}
                    color="primary"
                  >
                    <DownloadIcon />
                  </IconButton>
                  <IconButton onClick={handleClosePreview}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent
              sx={{
                p: 0,
                backgroundColor: "#000",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {loadingPreview ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={2}
                  py={8}
                >
                  <CircularProgress size={60} sx={{ color: "white" }} />
                  <Typography color="white">Loading preview...</Typography>
                </Box>
              ) : previewError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {previewError}
                </Alert>
              ) : fileContent && currentFile ? (
                <>
                  {currentFile.mime_type?.includes("image") ? (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "auto",
                        p: 2,
                      }}
                    >
                      <img
                        src={fileContent}
                        alt={currentFile.file_name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          transform: `scale(${zoomLevel})`,
                          transition: "transform 0.2s",
                        }}
                      />
                    </Box>
                  ) : currentFile.mime_type?.includes("pdf") ? (
                    <iframe
                      src={fileContent}
                      title={currentFile.file_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                    />
                  ) : null}

                  {/* Zoom Controls for Images */}
                  {currentFile.mime_type?.includes("image") && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        gap: 1,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        borderRadius: 3,
                        p: 1,
                      }}
                    >
                      <IconButton
                        onClick={handleZoomOut}
                        disabled={zoomLevel <= 0.5}
                        sx={{ color: "white" }}
                      >
                        <ZoomOutIcon />
                      </IconButton>
                      <Chip
                        label={`${Math.round(zoomLevel * 100)}%`}
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          minWidth: 70,
                        }}
                      />
                      <IconButton
                        onClick={handleZoomIn}
                        disabled={zoomLevel >= 3}
                        sx={{ color: "white" }}
                      >
                        <ZoomInIcon />
                      </IconButton>
                    </Box>
                  )}
                </>
              ) : null}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MedicalProfessionalOrderDetail;
